/**
 * The two pieces of the reseller company form that carry real rules, extracted
 * from `AdministrationResellersPage` so they can be tested without mounting the
 * table — the same reason `resellers-page-query.ts` exists next door.
 *
 * Both concern spec A §3.1–3.2: the reseller hierarchy (which is the shape of
 * the CRM scope wall, not an org-chart nicety) and the billing identity a
 * reseller-issued document is drawn in the name of.
 */
import type { CreateResellerData } from "@crm-src/lib/resellers-api"
import type { Reseller } from "@crm-src/types/lead"

/** The form's own state shape. Every value is a string because it comes from a
 * text input or a native <select>; the empty string is the "not set" value. */
export interface ResellerFormState {
  name: string
  code: string
  website: string
  address: string
  city: string
  state: string
  postal_code: string
  country: string
  parent_reseller_id: string
  legal_name: string
  tax_id: string
  currency: string
  payment_terms: string
  invoice_footer: string
  billing_email: string
  billing_phone: string
  assigned_salespeople_ids: string[]
}

/**
 * Candidate parents for `editing`: everything except itself and its own
 * descendants.
 *
 * crm-api rejects both — self-reference and cycles are validated, because the
 * recursive subtree CTE the whole reseller wall is derived from has to stay
 * acyclic and depth-bounded. Offering a choice that can only come back 422
 * teaches the operator the rule through a red toast instead of through the
 * list, so the impossible options are removed here.
 *
 * The descendant walk is a FIXED-POINT loop rather than recursion: the data can
 * be stale mid-edit, and a cycle that somehow reached the client must terminate
 * rather than blow the stack.
 */
export function resellerParentOptions(
  all: readonly Reseller[],
  editing: Reseller | null,
): Reseller[] {
  if (!editing) return [...all]

  const excluded = new Set<string>([String(editing.id)])
  let grew = true
  while (grew) {
    grew = false
    for (const r of all) {
      const parentId = r.parent_reseller_id != null ? String(r.parent_reseller_id) : null
      if (parentId && excluded.has(parentId) && !excluded.has(String(r.id))) {
        excluded.add(String(r.id))
        grew = true
      }
    }
  }
  return all.filter((r) => !excluded.has(String(r.id)))
}

/**
 * The request body for create/update.
 *
 * The one subtlety, and the reason this is a function rather than an inline
 * object literal: `parent_reseller_id` is sent as `""` when cleared, while
 * every other blank field is dropped as `undefined`. `undefined` is omitted
 * from the JSON, so an omitted parent would leave the EXISTING parent standing
 * — detaching a sub-reseller back to top level would silently do nothing.
 * crm-api reads a blank string as "no parent".
 */
export function buildResellerPayload(
  form: ResellerFormState,
  opts: { includeSalespeople: boolean },
): CreateResellerData {
  // Conditional spreads rather than `x || undefined`: the workspace compiles
  // with `exactOptionalPropertyTypes`, under which an explicit `undefined` is
  // NOT the same as an absent key. Spreading keeps blanks genuinely absent, so
  // "the operator left this empty" reaches crm-api as "don't set it" instead
  // of as a null that overwrites a stored value.
  return {
    name: form.name,
    ...(form.code ? { code: form.code } : {}),
    ...(form.website ? { website: form.website } : {}),
    ...(form.address ? { address: form.address } : {}),
    ...(form.city ? { city: form.city } : {}),
    ...(form.state ? { state: form.state } : {}),
    ...(form.postal_code ? { postal_code: form.postal_code } : {}),
    ...(form.country ? { country: form.country } : {}),
    // ALWAYS sent, even when empty — see this function's doc comment. An
    // omitted parent would leave the existing one standing, so detaching a
    // sub-reseller back to top level would silently do nothing.
    parent_reseller_id: form.parent_reseller_id,
    ...(form.legal_name ? { legal_name: form.legal_name } : {}),
    ...(form.tax_id ? { tax_id: form.tax_id } : {}),
    ...(form.currency ? { currency: form.currency } : {}),
    ...(form.payment_terms ? { payment_terms: form.payment_terms } : {}),
    ...(form.invoice_footer ? { invoice_footer: form.invoice_footer } : {}),
    ...(form.billing_email ? { billing_email: form.billing_email } : {}),
    ...(form.billing_phone ? { billing_phone: form.billing_phone } : {}),
    ...(opts.includeSalespeople && form.assigned_salespeople_ids.length > 0
      ? { assigned_salespeople_ids: form.assigned_salespeople_ids }
      : {}),
  }
}

/** Prefill the form from an existing reseller. `currency` falls back to USD
 * because crm-api declares the column NOT NULL DEFAULT 'USD' — posting "" on a
 * field the operator never touched would 422 the whole save. */
export function resellerFormFrom(reseller: Reseller): ResellerFormState {
  return {
    name: reseller.name,
    code: reseller.code || "",
    website: reseller.website || "",
    address: reseller.address || "",
    city: reseller.city || "",
    state: reseller.state || "",
    postal_code: reseller.postal_code || "",
    country: reseller.country || "U.S.A",
    parent_reseller_id: reseller.parent_reseller_id ? String(reseller.parent_reseller_id) : "",
    legal_name: reseller.billing?.legal_name || "",
    tax_id: reseller.billing?.tax_id || "",
    currency: reseller.billing?.currency || "USD",
    payment_terms: reseller.billing?.payment_terms || "",
    invoice_footer: reseller.billing?.invoice_footer || "",
    billing_email: reseller.billing?.billing_email || "",
    billing_phone: reseller.billing?.billing_phone || "",
    assigned_salespeople_ids: reseller.assigned_salespeople?.map((sp) => sp.id) || [],
  }
}

/** A blank form — a brand-new, top-level, USD reseller. */
export function emptyResellerForm(): ResellerFormState {
  return {
    name: "",
    code: "",
    website: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "U.S.A",
    parent_reseller_id: "",
    legal_name: "",
    tax_id: "",
    currency: "USD",
    payment_terms: "",
    invoice_footer: "",
    billing_email: "",
    billing_phone: "",
    assigned_salespeople_ids: [],
  }
}
